type EmptyObject = Record<string, never>;

/** Subset of `T` that {@link isEmpty} treats as empty at compile time. */
type Empty<T> =
  | Extract<T, '' | null | undefined>
  | (T extends EmptyObject ? T : never)
  | (T extends readonly unknown[] ? readonly [] & T : never);

/** Subset of `T` that {@link isNotEmpty} treats as non-empty at compile time. */
type NonEmpty<T> = T extends null | undefined
  ? never
  : T extends string
    ? Exclude<T, ''>
    : T extends readonly (infer Item)[]
      ? T & readonly [Item, ...Item[]]
      : T extends object
        ? T
        : T;

export function isEmpty<T>(value: T): value is Empty<T> {
  if (value === null || value === undefined) {
    return true;
  }

  if (typeof value === 'string') {
    return value.trim() === '';
  }

  if (Array.isArray(value)) {
    return value.length === 0;
  }

  if (value instanceof Map || value instanceof Set) {
    return value.size === 0;
  }

  if (typeof value === 'object') {
    return Object.keys(value as object).length === 0;
  }

  return false;
}

export function isNotEmpty<T>(value: T): value is NonEmpty<T> {
  return !isEmpty(value);
}
